import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import {
  BUG_REPORT_DESCRIPTION_MAX,
  BUG_REPORT_DESCRIPTION_MIN,
  BugReportService,
} from '../../core/services/bug-report.service';
import { ToastService } from '../../core/services/toast.service';

/**
 * "Report a bug" dialog, opened from the top bar. The user only describes the
 * problem; the page, browser and window size are attached automatically so the
 * team can reproduce it. Submissions land in the admin console's Bug reports
 * page and email the team.
 */
@Component({
  selector: 'app-bug-report-dialog',
  templateUrl: './bug-report-dialog.component.html',
})
export class BugReportDialogComponent implements OnChanges {
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();

  description = '';
  error: string | null = null;
  submitting = false;

  /** Captured when the dialog opens, so it reflects where the user hit the bug. */
  pageUrl = '';

  readonly maxLength = BUG_REPORT_DESCRIPTION_MAX;

  constructor(
    private readonly bugReports: BugReportService,
    private readonly toast: ToastService,
    private readonly router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue && !changes['open'].previousValue) {
      this.pageUrl = this.router.url;
      this.error = null;
    }
  }

  submit(): void {
    if (this.submitting) return;

    const description = this.description.trim();
    if (description.length < BUG_REPORT_DESCRIPTION_MIN) {
      this.error = 'Please tell us a little more about what went wrong.';
      return;
    }

    this.error = null;
    this.submitting = true;
    this.bugReports.submit({
      description,
      pageUrl:   this.pageUrl,
      userAgent: navigator.userAgent,
      viewport:  `${window.innerWidth}x${window.innerHeight}`,
    }).subscribe({
      next: () => {
        this.submitting = false;
        this.description = '';
        this.toast.show("Thanks — your bug report was sent. We'll take a look.");
        this.closed.emit();
      },
      error: (err: { error?: { message?: string } }) => {
        this.submitting = false;
        this.error = err?.error?.message ?? 'Could not send your report. Please try again.';
      },
    });
  }

  /** Keeps the draft so an accidental close doesn't lose what was typed. */
  cancel(): void {
    if (this.submitting) return;
    this.closed.emit();
  }
}
